import FormBuilderOb, {
  FormBuilderObOptions,
} from '@components/FormBuilder/FormBuilder.ob';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { usePatientStore } from '@store/patientStore';
import { FormIds } from '@utils/enums/FormIds.enum';
import { ErrorMessage, Loader } from '@gemini/lux-ds-pms';

import useGetInsuranceConfiguration from '@hooks/useGetInsuranceConfiguration';
import useGetCountryList from '@hooks/useGetCountryList';
import useGetCountryStatesList from '@hooks/useGetCountryStatesList';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useGetHoldersList from '@hooks/useGetHoldersList';
import useGetGenderList from '@hooks/useGetGenderList';
import { MultiplePatientsNavigator } from '@components/MultiplePatientsNavigator/MultiplePatientsNavigator';
import { NewPatientSection } from '@components/NewPatientSection/NewPatientSection';
import { Helmet } from 'react-helmet';
import { t } from 'i18next';
import useAnalytics from '@hooks/useAnalytics';
import { FlowsPages } from '@utils/enums/FlowsPages.enum';

interface PatientDetailsProps {
  setEnableButton: (val: boolean) => void;
}

const PatientDetailsPage = ({ setEnableButton }: PatientDetailsProps) => {
  const { trackPageAnalytics } = useAnalytics();

  const showAddNewPatientFrame = useOnlineBookingStore(
    state => state.showAddPatientFrame,
  );
  const currentPatientIndex = useOnlineBookingStore(
    state => state.currentPatient,
  );
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const brandName = useOnlineBookingStore(state => state.brandName);
  const organizationId = useOnlineBookingStore(
    state => state.generalInfo?.organizationId,
  );
  const practiceId = useOnlineBookingStore(state => state.practiceId) || '';

  const updatePatient = usePatientStore(state => state.updatePatient);
  const patients = usePatientStore(state => state.patients);

  //ANALYTICS (RONA only) - Personal Info Form
  useEffect(() => {
    if (brandName) {
      trackPageAnalytics({
        page: showAddNewPatientFrame
          ? FlowsPages.ADD_NEW_PATIENT
          : FlowsPages.PATIENT_DETAILS,
        patients,
      });
    }
  }, [showAddNewPatientFrame]);

  const {
    data: insurances,
    isLoading: isInsurancesLoading,
    isError: isErrorInInsurances,
  } = useGetInsuranceConfiguration({
    organizationId: brandName ? practiceId : organizationId,
  });

  const {
    data: genderList,
    isLoading: isGenderLoading,
    isError: isErrorInGender,
  } = useGetGenderList({
    organizationId,
  });

  const {
    data: holders,
    isLoading: isHoldersLoading,
    isError: isErrorInHolders,
  } = useGetHoldersList({
    organizationId,
  });

  const {
    data: countryList,
    isLoading: isCountryListLoading,
    isError: isErrorInCountryList,
  } = useGetCountryList();

  const {
    data: countryStatesList,
    isLoading: isCountryStatesListLoading,
    isError: isErrorInCountryStatesList,
  } = useGetCountryStatesList({ countryCode: 'US' });

  const [listEnableButton, setListEnableButton] = useState<
    Array<boolean | undefined>
  >([]);

  const enableButtonMaster = useMemo(() => {
    return listEnableButton.every(
      enable => (typeof enable === 'boolean' && enable) || enable === undefined,
    );
  }, [listEnableButton]);

  const formIdsList = useMemo(() => {
    return patients.map(p => {
      const resArray = [FormIds.PatientDetail];

      if (formConfigs && formConfigs.patientDetails.customFieldsPatient?.show) {
        resArray.push(FormIds.CustomFieldsPatient);
      }
      if (p?.insurance && p.insurance?.id !== '') {
        resArray.push(
          FormIds.PatientDetailPrimaryInsurance,
          FormIds.PatientDetailSecondaryInsurance,
        );
        if (
          formConfigs &&
          formConfigs.patientDetails.customFieldsInsurance?.show
        ) {
          resArray.push(FormIds.CustomFieldsInsurance);
        }
      }
      return resArray;
    });
  }, [formConfigs, patients]);

  const formOptionsList = useMemo(() => {
    return patients.map(p => {
      const resArray: Array<FormBuilderObOptions> = [
        {
          countryList: countryList,
          countryStatesList: countryStatesList,
          genderList: genderList,
        },
      ];
      if (formConfigs && formConfigs.patientDetails.customFieldsPatient?.show) {
        resArray.push({});
      }
      if (p?.insurance && p.insurance?.id !== '') {
        resArray.push(
          {
            insurances: insurances,
            holders: holders,
            genderList: genderList,
            countryList: countryList,
            countryStatesList: countryStatesList,
          },
          {
            insurances: insurances,
            holders: holders,
            genderList: genderList,
            countryList: countryList,
            countryStatesList: countryStatesList,
          },
        );
        if (
          formConfigs &&
          formConfigs.patientDetails.customFieldsInsurance?.show
        ) {
          resArray.push({});
        }
      }
      return resArray;
    });
  }, [
    countryList,
    countryStatesList,
    formConfigs,
    genderList,
    holders,
    insurances,
    patients,
  ]);

  const isLoading = useMemo(
    () =>
      isCountryListLoading ||
      isCountryStatesListLoading ||
      ((formIdsList.some(f =>
        f.includes(FormIds.PatientDetailPrimaryInsurance),
      ) ||
        formIdsList.some(f =>
          f.includes(FormIds.PatientDetailSecondaryInsurance),
        )) &&
        (isHoldersLoading || isGenderLoading || isInsurancesLoading)),
    [
      formIdsList,
      isCountryListLoading,
      isCountryStatesListLoading,
      isGenderLoading,
      isHoldersLoading,
      isInsurancesLoading,
    ],
  );

  const isError = useMemo(
    () =>
      isErrorInCountryList ||
      isErrorInCountryStatesList ||
      ((formIdsList.some(f =>
        f.includes(FormIds.PatientDetailPrimaryInsurance),
      ) ||
        formIdsList.some(f =>
          f.includes(FormIds.PatientDetailSecondaryInsurance),
        )) &&
        (isErrorInGender || isErrorInHolders || isErrorInInsurances)),
    [
      formIdsList,
      isErrorInCountryList,
      isErrorInCountryStatesList,
      isErrorInGender,
      isErrorInHolders,
      isErrorInInsurances,
    ],
  );

  const handleEnableButton = useCallback((index: number, enable: boolean) => {
    setListEnableButton(prev => {
      if (typeof prev[index] !== 'undefined' && prev[index] !== enable) {
        // Aggiorna solamente se cambia il valore
        const newVal = [...prev];
        newVal[index] = enable;
        return newVal;
      }
      return prev; // se non cambiano i valori ritorna lo stato vecchio
    });
  }, []);

  useEffect(() => {
    setEnableButton(enableButtonMaster);
  }, [enableButtonMaster]);

  useEffect(() => {
    setListEnableButton(patients.map(p => (p ? false : undefined)));
  }, [patients]);

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_patient_details_title')}</title>
      </Helmet>
      {showAddNewPatientFrame ? (
        <NewPatientSection setEnableButton={setEnableButton} />
      ) : isLoading ? (
        <Loader />
      ) : isError ? (
        <ErrorMessage>
          {/* TODO gestire msg di errore{error?.message} */}
        </ErrorMessage>
      ) : (
        <>
          {patients.map((_, index) => (
            <FormBuilderOb
              key={'patientDetails_' + index}
              isVisible={currentPatientIndex === index}
              formIds={formIdsList[index]}
              setEnableButton={enable => handleEnableButton(index, enable)}
              onUnmount={formData => {
                updatePatient(
                  index,
                  FormIds.PatientDetail,
                  formData,
                  insurances,
                );
              }}
              optionsList={formOptionsList[index]}
              patientIndex={index}
              mykey={`patientDetails_${index}`}
            />
          ))}
        </>
      )}
      {!showAddNewPatientFrame && (
        <MultiplePatientsNavigator
          style={{ padding: '0rem 0rem 1.5rem 0rem' }}
        />
      )}
    </>
  );
};

export default PatientDetailsPage;
